import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledTableHead from 'Components/StyledTableHead'
import { WithLoader } from 'Components/WithLoader'
import { AffiliateOrderLineRow } from 'Services/SalesManagement/Features/Order/Components/AffiliateOrderLineRow'
import { AffiliatePaymentMethod } from 'Services/SalesManagement/Features/Order/Components/AffiliatePaymentEventRow'
import { RefundHistoryList } from 'Services/SalesManagement/Features/Order/Components/RefundHistoryList'
import WalttiPosOrderRefundDialog from 'Services/SalesManagement/Features/Order/Components/WalttiPosOrderRefundDialog'
import {
  useGetWalttiPosOrderRefundListQuery,
  useGetWalttiPosOrderRefundOptionsQuery
} from 'Services/SalesManagement/Features/Order/orderApi'
import {
  fetchWalttiPosOrder,
  fetchWalttiPosOrderReceipt
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import fileDownload from 'Util/fileDownload'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import DownloadIcon from '@mui/icons-material/CloudDownload'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function WalttiPosOrderDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/orders/walttipos-orders`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false)

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const { singleWalttiPosOrder, loading, affiliateOrderReceiptLoading } = useTypedSelector(
    state => state.order
  )

  const { data: walttiPosOrderRefundOptions, isLoading: isRefundOptionsLoading } =
    useGetWalttiPosOrderRefundOptionsQuery({ id: instanceIdParam })

  const {
    data: walttiPosOrderRefundList = [],
    isLoading: isRefundListLoading,
    refetch: refetchWalttiPosOrderRefundList
  } = useGetWalttiPosOrderRefundListQuery({ id: instanceIdParam })

  useEffect(() => {
    const params = {
      id: instanceIdParam
    }
    dispatch(fetchWalttiPosOrder(params))
  }, [dispatch, instanceIdParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleReceiptDownload = (id: number) => {
    const params = {
      id
    }
    dispatch(fetchWalttiPosOrderReceipt(params))
      .then(unwrapResult)
      .then(response => {
        const fileNamePrefix = t`Waltti point of sale order receipt`
        const fileName = `${fileNamePrefix}, ${singleWalttiPosOrder?.external_id}`
        fileDownload(response, fileName)
      })
      .catch(error => {
        SnackbarUtils.error(error.error)
      })
  }

  const handleRefundRequestSuccess = () => {
    // Re-fetch order and refund data to update the UI
    dispatch(fetchWalttiPosOrder({ id: instanceIdParam }))
    refetchWalttiPosOrderRefundList()
  }

  const getRefundText = () => {
    if (singleWalttiPosOrder?.refunded_by !== null) {
      return t`Order already refunded`
    }
    if (singleWalttiPosOrder?.refund_requested) {
      return t`Refund requested`
    }
    return t`Request refund`
  }

  const isRefundAllowed = () => {
    // Check if the order is already refunded.
    // The sales points can automatically refund the order, in which case there will be no separate refund request.
    if (singleWalttiPosOrder?.refunded_by !== null) {
      return false
    }
    if (singleWalttiPosOrder?.refund_requested) {
      return false
    }
    return true
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Orders`
    },
    {
      title: t`Point of sale orders`,
      link: baseUrl
    },
    {
      title: singleWalttiPosOrder ? singleWalttiPosOrder.external_id : ''
    }
  ]

  const isLoading = loading === 'pending'
  const isAffiliateOrderReceiptLoading = affiliateOrderReceiptLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <Box>
          <BackButton label={t`Back`} link={previousLocation} />
          {singleWalttiPosOrder && (
            <>
              <CustomButton
                disabled={!isRefundAllowed()}
                title={getRefundText()}
                onClick={() => setIsRefundDialogOpen(true)}
                sx={{ float: 'right', mb: 1, ml: 1 }}
              >
                {getRefundText()}
              </CustomButton>
              {isRefundAllowed() && (
                <WalttiPosOrderRefundDialog
                  orderId={singleWalttiPosOrder.id}
                  open={isRefundDialogOpen}
                  walttiPosOrderRefundOptions={walttiPosOrderRefundOptions}
                  isRefundOptionsLoading={isRefundOptionsLoading}
                  onClose={() => setIsRefundDialogOpen(false)}
                  onSuccess={handleRefundRequestSuccess}
                />
              )}
              <CustomLoadingButton
                isLoading={isAffiliateOrderReceiptLoading}
                onClick={() => handleReceiptDownload(singleWalttiPosOrder.id)}
                type="submit"
                icon={<DownloadIcon />}
                iconPosition="start"
                sx={{ float: 'right', mb: 1 }}
                size="medium"
              >{t`Download receipt`}</CustomLoadingButton>
            </>
          )}
        </Box>
      </Box>
      <WithLoader loading={isLoading}>
        <Box>
          {singleWalttiPosOrder && (
            <Box>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Order details table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={2}>{t`Order details`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{t`External ID`}</TableCell>
                      <TableCell>{singleWalttiPosOrder.external_id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Affiliate identity ID`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleWalttiPosOrder.affiliate_identity_id)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Travel card number`}</TableCell>
                      <TableCell>
                        {' '}
                        {singleWalttiPosOrder.travel_card_numbers.length > 0
                          ? singleWalttiPosOrder.travel_card_numbers.toString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Waltti ID`}</TableCell>
                      <TableCell>{showValueOrHyphen(singleWalttiPosOrder.waltti_user)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`User email`}</TableCell>
                      <TableCell>{showValueOrHyphen(singleWalttiPosOrder.user_email)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Operator`}</TableCell>
                      <TableCell>{singleWalttiPosOrder.operator_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Sales channel`}</TableCell>
                      <TableCell>{singleWalttiPosOrder.sales_channel_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Client application version`}</TableCell>
                      <TableCell>{singleWalttiPosOrder.client_application_version}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Created`}</TableCell>
                      <TableCell>{datetimeFormatter(singleWalttiPosOrder.created)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Product scheme`}</TableCell>
                      <TableCell>{singleWalttiPosOrder.product_scheme}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Receipt information`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(singleWalttiPosOrder.receipt_information)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t`Payment method`}</TableCell>
                      <TableCell>
                        {showValueOrHyphen(
                          singleWalttiPosOrder.payment_method?.payment_method_name
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Ticket details table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={9}>{t`Tickets`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t`Order line ID`}</TableCell>
                      <TableCell>{t`Ticket ID`}</TableCell>
                      <TableCell>{t`Product ID`}</TableCell>
                      <TableCell>{t`Product name`}</TableCell>
                      <TableCell>{t`Customer group`}</TableCell>
                      <TableCell>{t`Municipality`}</TableCell>
                      <TableCell>{t`Start zone`}</TableCell>
                      <TableCell>{t`End zone`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleWalttiPosOrder.ticket_order_lines.length > 0 ? (
                      <>
                        {singleWalttiPosOrder.ticket_order_lines.map(line => (
                          <AffiliateOrderLineRow key={line.ticket.ticket_id} row={line} />
                        ))}
                      </>
                    ) : (
                      <TableRow>
                        <TableCell>{t`No order lines available`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table aria-label={t`Payment info table`}>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell colSpan={5}>{t`Payment info`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t`Transactional ID`}</TableCell>
                      <TableCell>{t`Created`}</TableCell>
                      <TableCell>{t`Payment Operator`}</TableCell>
                      <TableCell>{t`Card PAN`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {singleWalttiPosOrder.payment_method ? (
                      <AffiliatePaymentMethod
                        payment_method={singleWalttiPosOrder.payment_method}
                      />
                    ) : (
                      <TableRow>
                        <TableCell>{t`No payment info available`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <RefundHistoryList
                refundList={walttiPosOrderRefundList}
                affiliateOrderRefundOptions={walttiPosOrderRefundOptions}
                isRefundOptionsLoading={isRefundOptionsLoading}
                isRefundListLoading={isRefundListLoading}
              />
            </Box>
          )}
        </Box>
      </WithLoader>
    </Box>
  )
}
